import { useEffect, useRef, useState } from "react";
import {
  isDistinctMarker,
  pointerPctWithinTrack,
  sliderFillPct,
} from "./slider-math";

const GHOST_VARIANTS = [
  { height: "70%", top: "15%" },
  { height: "60%", top: "20%" },
];

/** Pull this far below the track centerline to arm release-to-lock. */
const PITCH_LOCK_BELOW_THRESHOLD_PX = 28;

export default function ControlSlider({
  label,
  min,
  max,
  step,
  value,
  markerPcts,
  disabled = false,
  onChange,
  ghostEnabled = false,
  pitchLockGesture = null,
}) {
  const trackRef = useRef(null);
  const pitchLockGestureRef = useRef(pitchLockGesture);
  const [ghost, setGhost] = useState({
    active: false,
    pct: 0,
    ...GHOST_VARIANTS[0],
  });
  const [pitchLockArmed, setPitchLockArmed] = useState(false);

  useEffect(() => {
    pitchLockGestureRef.current = pitchLockGesture;
  }, [pitchLockGesture]);

  const fillPct = sliderFillPct({ min, max, value });
  const markerPoints = markerPcts.filter((pct) =>
    isDistinctMarker(pct, fillPct),
  );
  const preservePitchMode = !!pitchLockGesture?.preservePitchMode;
  const pitchLockHint = preservePitchMode
    ? "Release to unlock pitch"
    : "Release to lock pitch";

  const setGhostAt = (event, active) => {
    if (!ghostEnabled || disabled) return;

    const variant = GHOST_VARIANTS[Math.floor(Math.random() * GHOST_VARIANTS.length)];
    setGhost({
      active,
      pct: pointerPctWithinTrack(event.clientX, event.currentTarget),
      ...variant,
    });
  };

  const onPitchLockPointerDown = (event) => {
    if (!pitchLockGesture || disabled || event.button !== 0) return;

    const track = trackRef.current;
    if (!track) return;

    let armed = false;
    setPitchLockArmed(false);

    const updateArmed = (clientY) => {
      const rect = track.getBoundingClientRect();
      const trackCenterY = rect.top + rect.height / 2;
      const nextArmed = clientY >= trackCenterY + PITCH_LOCK_BELOW_THRESHOLD_PX;
      if (nextArmed !== armed) {
        armed = nextArmed;
        setPitchLockArmed(nextArmed);
      }
    };

    updateArmed(event.clientY);

    const onMove = (moveEvent) => {
      updateArmed(moveEvent.clientY);
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);

      if (armed) {
        const gesture = pitchLockGestureRef.current;
        if (gesture) {
          gesture.onPreservePitchModeChange(!gesture.preservePitchMode);
        }
      }
      setPitchLockArmed(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return (
    <div
      className={[
        "slider",
        pitchLockGesture && "slider--pitch-lock-gesture",
        preservePitchMode && "slider--pitch-locked",
        pitchLockArmed && "slider--pitch-lock-armed",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="slider__label">{label}</div>
      <div
        ref={trackRef}
        className="slider__track"
        onPointerDown={pitchLockGesture ? onPitchLockPointerDown : undefined}
        onMouseEnter={ghostEnabled ? (event) => setGhostAt(event, true) : undefined}
        onMouseMove={
          ghostEnabled
            ? (event) => setGhostAt(event, ghost.active)
            : undefined
        }
        onMouseLeave={
          ghostEnabled ? () => setGhost((current) => ({ ...current, active: false })) : undefined
        }
        onMouseDown={
          ghostEnabled ? () => setGhost((current) => ({ ...current, active: false })) : undefined
        }
        onMouseUp={ghostEnabled ? (event) => setGhostAt(event, true) : undefined}
      >
        <div className="slider__points slider__points--markers" aria-hidden="true">
          {markerPoints.map((pointPct) => (
            <div
              key={pointPct}
              className="slider__point"
              style={{ "--point-pct": pointPct }}
            />
          ))}
        </div>

        <div className="slider__points slider__points--active" aria-hidden="true">
          <div
            className="slider__point slider__point--active"
            style={{ "--point-pct": fillPct }}
          />
        </div>

        {ghostEnabled && (
          <div
            className={`slider__ghost ${ghost.active ? "slider__ghost--active" : ""}`}
            style={{
              "--point-pct": ghost.pct,
              height: ghost.height,
              top: ghost.top,
            }}
          />
        )}

        {pitchLockGesture && (
          <div
            className={`slider__pitch-lock-hint ${
              pitchLockArmed ? "slider__pitch-lock-hint--visible" : ""
            }`}
            style={{ "--point-pct": fillPct }}
            aria-hidden={!pitchLockArmed}
          >
            {pitchLockHint}
          </div>
        )}

        {preservePitchMode && !pitchLockArmed && (
          <div
            className="slider__pitch-locked-label"
            style={{ "--point-pct": fillPct }}
          >
            Locked pitch
          </div>
        )}

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
    </div>
  );
}
